<script lang="ts">import { createEventDispatcher } from 'svelte';
import Icon from '../Icon/Icon.svelte';
import Count from '../Count/Count.svelte';
import { GLOBAL_NAV_DESKTOP_CART_CONTENT_ID } from '../../../../../js-v2/constants';
import { blurOnClick } from '../../../../../js-v2/blurOnClick';
import { getCartCount } from '../../../../../js-v2/stores/user-state-stores';
import { onTouchOrClick } from '../../../../../js-v2/onTouchOrClick';
const dispatch = createEventDispatcher();
const countStore = getCartCount();
const { isOpen = false, title = 'Your cart', url = '' } = $props();
function toggle() {
    dispatch('toggle');
}
// Goal: make mouse click on desktop go to cart url
function onClick(e) {
    // If this handler is fired, it was not a touch because the
    // onTouchOrClick action below would have cancelled the mouse events.
    // Furthermore, we can tell if the event was actually a mouse
    // click (i.e. not a keyboard click) by looking at e.detail:
    // https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail
    // So, if url was passed and this was a mouse click...
    if (url && e.detail !== 0) {
        // ...act like a link and manually go to url
        window.location.href = url;
    }
}
</script>

<button
  class="Toggle"
  aria-haspopup="true"
  aria-expanded={isOpen}
  aria-controls={`nav-flyout-${GLOBAL_NAV_DESKTOP_CART_CONTENT_ID}`}
  onclick={onClick}
  use:onTouchOrClick={toggle}
  use:blurOnClick
>
  <Icon isEmpty={$countStore === 0} />
  {#if $countStore > 0}
    <Count count={$countStore} />
  {/if}
  <span class="title">{title}</span>
</button>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.Toggle {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, var(--color-neutral-000, #000000));
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 10px;
  transition: none;
}
.Toggle:focus-visible:not(:active) {
  outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
  border-radius: var(--accessibility-focus-outline-border-radius);
  outline-offset: var(--accessibility-focus-outline-offset);
}
.Toggle:focus {
  background: transparent;
  outline: none;
}
.Toggle:hover {
  background: transparent;
}
.Toggle .title {
  position: absolute;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
}</style>
